import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";
import { RpaButtonLink } from "../../../components/RpiLink";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { RpiRouteLink } from "../../../hooks/useNavigateRoute";
import { createDrawerOverlay, createRpiDrawerView, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { RpaDrawer, RpaDrawerOverlay } from "../../../rpi-core/drawer/RpaDrawer";
import { RpaDrawerField } from "../../../rpi-core/drawer/RpaDrawerField";
import { dashboardUserRoleOptions } from "../../../utils/graphql.util";
import { shouldRetry } from "../../../utils/query.util";

const FORM_ID = "dashboard-user-edit-form";

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
  },
});

export const DashboardUserEditView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { id: stringId } = useParams();
    const id = React.useMemo(() => stringId && Number.parseInt(stringId), []);

    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    React.useEffect(() => {
      if (!stringId) handleClose();
    }, [stringId]);

    const { data, isLoading, error } = GraphQL.useDashboardUserQuery(
      { id: id || -1 },
      {
        enabled: !!stringId && opened,
        refetchOnMount: true,
        retry: shouldRetry(handleClose),
      }
    );
    const dashboardUser = React.useMemo(() => data?.dashboardUser, [data]);

    return (
      <RpaDrawer title="Edit User" isLoading={isLoading} error={error} opened={opened} onClose={handleClose}>
        {dashboardUser && (
          <EditUser dashboardUser={dashboardUser} onSuccess={() => handleClose(true)} onCancel={() => handleClose()} />
        )}
      </RpaDrawer>
    );
  }
);

interface EditUserProps {
  dashboardUser: GraphQLQueryData<GraphQL.DashboardUserQuery>;
  onSuccess: () => void;
  onCancel: () => void;
}

const EditUser: React.FC<EditUserProps> = ({ dashboardUser, onSuccess, onCancel }) => {
  const { classes } = useStyles();
  const user = useAuthUser();
  const queryClient = useQueryClient();

  const {
    mutate: editDashboardUser,
    reset,
    ...mutation
  } = GraphQL.useEditDashboardUserMutation<{ message: string }>({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["DashboardUser"] }),
  });

  const form = useForm<GraphQL.EditDashboardUserMutationVariables["input"]>({
    validateInputOnBlur: true,
    initialValues: {
      username: dashboardUser.username || "",
      firstName: dashboardUser.firstName || "",
      lastName: dashboardUser.lastName || "",
      customerPublicId: dashboardUser.customerPublicId || "",
      role: dashboardUser?.role || GraphQL.DashboardUserRole.User,
    },
    validate: {
      firstName: (value: string) => (value.trim().length >= 2 ? null : "Please enter a valid first name"),
      lastName: (value: string) => (value.trim().length >= 2 ? null : "Please enter a valid last name"),
    },
  });

  React.useEffect(() => reset, []);

  const handleSubmit = React.useCallback(
    (input: typeof form.values) => {
      editDashboardUser({ id: dashboardUser.id, input }, { onSuccess });
    },
    [editDashboardUser, dashboardUser, onSuccess]
  );

  const overlay = React.useMemo(
    () =>
      createDrawerOverlay({
        loading: {
          message: "Editing user...",
          description: "Please wait, do not close this screen.",
        },
        error: {
          message: mutation.error?.message || "Failed to edit user...",
          description: "Please try again later.",
        },
        success: {
          message: "User edited!",
        },
      })(mutation),
    [mutation]
  );

  const roleSelectData = React.useMemo(() => dashboardUserRoleOptions(user.is.SUPER_ADMIN), [user.is.SUPER_ADMIN]);

  if (overlay) return <RpaDrawerOverlay overlay={false} {...overlay} />;

  return (
    <form id={FORM_ID} onSubmit={form.onSubmit(handleSubmit)}>
      <div className={classes.body}>
        <RpaTextInput label="First Name" placeholder="Enter" {...form.getInputProps("firstName")} />
        <RpaTextInput label="Last Name" placeholder="Enter" {...form.getInputProps("lastName")} />
        <RpaDropdown label="Role" placeholder="Select.." data={roleSelectData} {...form.getInputProps("role")} />

        <RpaDrawerField label="Username">{dashboardUser.username}</RpaDrawerField>
        <RpaDrawerField label="Email">{dashboardUser.email}</RpaDrawerField>

        {user.is.SUPER_ADMIN && dashboardUser.customerPublicId && (
          <RpaDrawerField label="Organization">
            <RpiRouteLink variant="inherit" route={["customer", { publicId: dashboardUser.customerPublicId }]}>
              {dashboardUser.customerPublicId}
            </RpiRouteLink>
          </RpaDrawerField>
        )}
      </div>
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button form={FORM_ID} type="submit" variant="filled" intent="primary" size="large" fullWidth>
            Save Changes
          </Button>
          <RpaButtonLink onClick={onCancel}>Cancel</RpaButtonLink>
        </div>
      </RpaDrawer.Footer>
    </form>
  );
};
